import { Link } from '@ps/web-shared-components';
import classNames from 'clsx';
import { kebabCase } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import URL from 'url-parse';

import type { CasinoIcon } from '@swf/casino';
import { getIconWidth, getRenderIcon, getSLIconFallback } from '@swf/casino';
import LazyImage from '@swf/components/LazyImage/LazyImage';
import FSBIcon from '@swf/core/assets/casino/fsb-placeholder-sl.png';
import useTouchSupported from '@swf/core/hooks/useTouchSupported';
import { select } from '@swf/core/store';
import type { CasinoGame, MosaicContext, TileType } from '@swf/core/types/CasinoGame';

import styles from './CasinoGameIconSL.scss';

export interface Props {
  className?: string;
  disabled?: boolean;
  game?: CasinoGame;
  images?: CasinoGame['images'];
  FSBPlaceholder?: boolean;
  icon?: CasinoIcon;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  preload?: boolean;
  innerRef?: React.Ref<HTMLDivElement>;
  scale?: boolean;
  tooltip?: string;
  visible?: boolean;
  mosaicHint?: MosaicContext;
  tileType: TileType;
}

interface PendingProps {
  setLoaded: React.Dispatch<React.SetStateAction<boolean>>;
}

const tilesTypeMap = {
  poster: 'icnPoster',
  wide: 'icnWide'
};

const Pending: React.FC<PendingProps> = ({ setLoaded }) => {
  useEffect(() => () => setLoaded(true), [setLoaded]);
  return null;
};

const CasinoGameIconSL: React.FC<React.PropsWithChildren<Props>> = ({
  images,
  game,
  scale = true,
  disabled,
  tooltip,
  innerRef,
  visible,
  FSBPlaceholder,
  tileType,
  ...rest
}) => {
  const isMobile = useSelector(select.device.isMobile);
  const isTouchSupported = useTouchSupported();
  const { name = '' } = game || {};
  const fallback = FSBPlaceholder ? FSBIcon : getSLIconFallback(tileType);
  const validImages = useMemo(() => images || game?.images || {}, [images, game]);
  const widthFG = useMemo(() => getIconWidth('icnForeground', isMobile), [isMobile]);
  const widthBG = useMemo(() => getIconWidth('icnBackground', isMobile), [isMobile]);
  const gameBGIcon = useMemo(
    () => getRenderIcon('icnBackground', isMobile, validImages),
    [isMobile, validImages]
  );
  const gameFGIcon = useMemo(
    () => getRenderIcon('icnForeground', isMobile, validImages),
    [isMobile, validImages]
  );
  const [fgLoaded, setFgLoaded] = useState(false);
  const [bgLoaded, setBgLoaded] = useState(false);

  const [srcFG, srcBG] = useMemo((): [string, string] => {
    if (!gameBGIcon || !gameFGIcon) {
      return ['', ''];
    }

    const urlFG = new URL(gameFGIcon);
    const urlBG = new URL(gameBGIcon);

    if (game && widthFG && scale) {
      urlFG.set('query', {
        imwidth: Math.floor(widthFG).toString(),
        id: `${game.gametype}${game.variant}`
      });
    }

    if (game && widthBG && scale) {
      urlBG.set('query', {
        imwidth: Math.floor(widthBG).toString(),
        id: `${game.gametype}${game.variant}`
      });
    }

    return [urlFG.toString(), urlBG.toString()];
  }, [gameFGIcon, gameBGIcon, widthFG, widthBG, scale, game]);

  const aliases = useSelector(select.cms.getAliases);
  const routeAvailable = aliases && aliases.casinoGameInfo;

  useEffect(() => {
    const fgImgElement = srcFG && document.querySelector(`img[src="${srcFG}"]`);
    const bgImgElement = srcBG && document.querySelector(`img[src="${srcBG}"]`);

    if (fgImgElement) {
      setFgLoaded(true);
    }

    if (bgImgElement) {
      setBgLoaded(true);
    }
  }, [srcFG, srcBG]);

  const propsFG = {
    className: classNames(styles.icon, (!fgLoaded || !bgLoaded) && styles.hidden),
    src: srcFG
  };
  const propsBG = {
    className: classNames(styles.icnBackground, (!fgLoaded || !bgLoaded) && styles.hidden),
    src: srcBG
  };

  const fallbackElement = (
    <>
      {name && <div className={styles.iconLabel}>{name}</div>}
      <img
        className={classNames(styles.icnBackground, styles.fallback, FSBPlaceholder && styles.fsb)}
        src={fallback}
        alt={tooltip || name}
        suppressHydrationWarning
      />
    </>
  );

  const gameIconElem =
    srcBG && srcFG ? (
      <>
        {!fgLoaded && !bgLoaded && fallbackElement}
        <LazyImage
          {...propsFG}
          visible={visible}
          alt={tooltip || name}
          loader={<Pending setLoaded={setFgLoaded} />}
        />
        <LazyImage
          {...propsBG}
          visible={visible}
          alt={tooltip || name}
          loader={<Pending setLoaded={setBgLoaded} />}
        />
      </>
    ) : (
      fallbackElement
    );
  return (
    <div
      onClick={rest.onClick}
      className={classNames(
        styles.container,
        (!game || disabled) && styles.disabled,
        isTouchSupported && styles.touch,
        rest.className
      )}
      ref={innerRef}
    >
      {routeAvailable && game ? (
        <Link
          className={classNames(styles.link, styles[tilesTypeMap[tileType] as keyof typeof styles])}
          track={false}
          onClick={(event: React.MouseEvent) => event.preventDefault()}
          alias="casinoGameInfo"
          values={{ name: kebabCase(game.name), gtype: game.gametype, vtype: game.variant }}
        >
          {gameIconElem}
        </Link>
      ) : (
        gameIconElem
      )}
      {rest.children}
    </div>
  );
};

export default CasinoGameIconSL;
